import React, { useEffect } from 'react';
import SvgIcon from '../../components/SvgIcon';
import { colors } from '../../lib/colors';
import dayjs, { Dayjs } from 'dayjs';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import Dropdown from '../../components/DropDown/DropDown';
import { CodeValueType } from '../../types/types';
import NewProfileImage from '../../components/NewProfileImage';
import ModalSalaryCalIncentive from './ModalSalaryCalIncentive';
import { CommissionType } from '../../api/salesCommision';
import ModalAddEtcSalary from './ModalAddEtcSalary';
import { TooltipIcon } from './SettlementCalendar';
import { EtcBonusAmountType, RevenueAdjustment, SalaryCalcData, SalaryCalcScheduleBracket } from '../../api/salaryCalc';
import useSalaryCalcMutationService from '../../services/useSalaryCalcMutationService';
import { cloneDeep, isEqual } from 'lodash';
import ModalNoSaveChangeVariable from './ModalNoSaveChangeVariable';
import RealDropDown from '@/components/DropDown/RealDropDown';
import { SettlementType } from '../../features/centerSettlement/types';
import { useCoachMemberSessionOffsets } from '../../features/coachMemberSessionOffset';
import { useCoachCommissionTables } from '@/features/coachCommissionTable';
import { overlay } from 'overlay-kit';
import ModalSessionOffsetEdit from './ModalSessionOffsetEdit';
import ModalRevenueAdjustment from './ModalRevenueAdjustment';
import SettlementLoadError from './components/SettlementLoadError';
import {
    AllCoachesSalaryTable,
    buildAllCoachesRow,
    computeBracketMissing,
    buildCoachSalaryExcelValue,
    CoachSalaryExcelValue,
    computeSalary,
    etcBonusToKRW,
    exportAllCoachesSalaryExcel,
    resolveVatSettings,
    salaryTableCoaches,
    summarizeAllCoachesRows,
    useAllCoachesSalary,
    formatCommissionRateText,
    resolveAppliedRuleSummaryDisplay,
    resolveInOutDisplay,
    resolveForcedInNotes,
    inLineTitle,
    inLineNote,
    formatAppliedRuleSummaryLabel,
    resolveCommissionUnit,
    CommissionUnit,
} from '../../features/salaryCalc';
import {
    buildCategoryNameMap,
    useSettlementCategories,
    SETTLEMENT_TRANSFER_LOOKUP_FAILED,
} from '../../features/settlementClassification';

type Props = {
    date: string;
    onMonthNavigate: (date: Date | string | Dayjs) => void;
    coachList: CodeValueType[];
    selectedCoach?: CodeValueType;
    onCoachSelect: (coach: CodeValueType) => void;
    salaryCalcData: SalaryCalcData;
    settlementType?: SettlementType;
    includeGroupClassInSettlement?: boolean;
    refresh: any;
    /** 급여 숫자를 불러오지 못했을 때(예: 503 재등록 수업료 조회 실패) 본문 대신 보일 문구 */
    loadErrorMessage?: string;
    /** 「구간 미설정」 안내의 「설정하기」 — 기존 수업 정산 비율 설정 창을 연다. 없으면 안내 문구만 보인다. */
    onOpenCommissionSetting?: () => void;
    /** 요약 칸 숫자는 computeSalary 결과 그대로(엑셀에서 다시 계산하지 않는다) */
    onClickExcel: (
        value: CoachSalaryExcelValue,
        coachId: string,
    ) => void;
};
type SalaryView = 'coach' | 'all';
type NavigateInfo = { type: 'month' | 'coach' | 'view' | 'viewCoach'; payload: any };

/** 「강사별 / 전체 강사」 전환 */
const SalaryViewToggle = ({ value, onChange }: { value: SalaryView; onChange: (next: SalaryView) => void }) => (
    <div role="group" aria-label="급여 보기" className="flex items-center bg-[#F2F4F6] rounded-[50px] p-[4px] mr-[14px]">
        {(
            [
                { key: 'coach', label: '강사별' },
                { key: 'all', label: '전체 강사' },
            ] as { key: SalaryView; label: string }[]
        ).map((item) => (
            <button
                key={item.key}
                type="button"
                aria-pressed={value === item.key}
                onClick={() => value !== item.key && onChange(item.key)}
                className={`px-[16px] py-[8px] rounded-[50px] text-[15px] leading-[20px] ${
                    value === item.key ? 'bg-white text-primary font-noto-medium shadow-sm' : 'text-[#6B7280]'
                }`}
            >
                {item.label}
            </button>
        ))}
    </div>
);

const SalaryCalculationPage = ({
    date,
    onMonthNavigate,
    coachList,
    selectedCoach,
    onCoachSelect,
    salaryCalcData,
    settlementType = 'base',
    includeGroupClassInSettlement = false,
    refresh,
    loadErrorMessage,
    onOpenCommissionSetting,
    onClickExcel,
}: Props) => {
    const authObject = getItemWithExpiration('authObject');
    // 센터 부가세 정책 — 전체 강사 표와 같은 함수로 읽는다
    const { vatPolicy, vatRate } = resolveVatSettings(authObject);
    const salaryMonthLabel = dayjs(date).format('YYYY년 MM월');
    const groupSettlementLabel = includeGroupClassInSettlement ? '그룹수업 합산 ON' : '그룹수업 합산 OFF';

    const isSessionTier = settlementType === 'sessionTier';
    const totalSessionCount = salaryCalcData?.totalSessionCount ?? 0;
    const bracketBreakdown = salaryCalcData?.bracketBreakdown ?? [];

    // 누적 회차 보정 (sessionTier 전용) — 모달에서 편집, 여기서는 미리보기 데이터만 조회
    const selectedCoachId = selectedCoach?.code ?? '';
    const sessionOffsetYear = dayjs(date).year().toString();
    const sessionOffsetMonth =
        dayjs(date).month() + 1 < 10 ? `0${dayjs(date).month() + 1}` : `${dayjs(date).month() + 1}`;
    const { data: sessionOffsetsData } = useCoachMemberSessionOffsets({
        centerId: authObject?.centerId || '',
        coachId: selectedCoachId,
        year: sessionOffsetYear,
        month: sessionOffsetMonth,
        enabled: isSessionTier && !!authObject?.centerId && !!selectedCoachId && selectedCoachId !== 'all',
    });

    // TODO: 백엔드 coachMemberSessionOffsets API 연동 후 더미 제거
    const DUMMY_SESSION_OFFSET_MEMBERS = React.useMemo(
        () => [
            { memberId: 'dummy-1', memberName: '홍길동', rappoCount: 7, offset: 44, cumulativeSessionCount: 51 },
            { memberId: 'dummy-2', memberName: '김민수', rappoCount: 3, offset: 0, cumulativeSessionCount: 3 },
            { memberId: 'dummy-3', memberName: '이지은', rappoCount: 12, offset: 18, cumulativeSessionCount: 30 },
            { memberId: 'dummy-4', memberName: '박서연', rappoCount: 0, offset: 25, cumulativeSessionCount: 25 },
            { memberId: 'dummy-5', memberName: '정우성', rappoCount: 9, offset: 0, cumulativeSessionCount: 9 },
        ],
        [],
    );
    const sessionOffsetMembers = React.useMemo(() => {
        const serverMembers = sessionOffsetsData?.members;
        if (serverMembers && serverMembers.length > 0) return serverMembers;
        return DUMMY_SESSION_OFFSET_MEMBERS;
    }, [sessionOffsetsData?.members, DUMMY_SESSION_OFFSET_MEMBERS]);

    const openSessionOffsetModal = () => {
        if (!authObject?.centerId || !selectedCoachId || selectedCoachId === 'all') return;
        overlay.open(({ unmount }) => (
            <ModalSessionOffsetEdit
                onClose={unmount}
                centerId={authObject.centerId}
                coachId={selectedCoachId}
                coachName={selectedCoach?.value ?? ''}
                members={sessionOffsetMembers}
            />
        ));
    };
    const { postSalaryCalcsMutation } = useSalaryCalcMutationService({
        onPostSalaryCalcsSuccess() {
            console.log('저장완료');
        },
    });

    const bassicSalaryRef = React.useRef<HTMLInputElement>(null);
    // 수업 수강권 매출액 (그룹 — 토글 OFF 시 서버가 0 반환)
    const groupSales = salaryCalcData?.groupSales ?? 0;

    // 매출 조정 (백엔드 확장 전 — 더미 동작)
    //  - refundAmount: 백엔드가 자동 계산해서 내려줄 값. 음수(차감)로 정의. 미구현 동안 더미 값 노출.
    //  - revenueAdjustments: 저장된 조정 항목. local state 로 관리해 모달 저장 시 즉시 반영(백엔드 연동 시 postSalaryCalcsMutation 으로 교체 예정)
    const refundAmount = salaryCalcData?.refundAmount ?? -150_000;
    const [revenueAdjustments, setRevenueAdjustments] = React.useState<RevenueAdjustment[]>(
        () => salaryCalcData?.revenueAdjustments ?? [],
    );
    // 매출액·자동 집계 매출액·매출 조정 합은 아래 computeSalary 결과를 쓴다(식 한 벌 — features/salaryCalc)
    // 매출 조정 모달 오픈
    const [isRevenueAdjustmentModal, setIsRevenueAdjustmentModal] = React.useState(false);

    // 매출 구간 표 (tier 정산용)
    //  - salaryCalcData 에 inScheduleBrackets/outScheduleBrackets 가 있으면 우선 사용
    //  - 없으면 coachCommissionTables 에서 폴백 (백엔드 확장 전 더미 소스)
    const isTierSettlement = settlementType === 'tier';
    const yearStr = dayjs(date).year().toString();
    const monthStr2 = dayjs(date).month() + 1 < 10 ? `0${dayjs(date).month() + 1}` : `${dayjs(date).month() + 1}`;
    const { data: commissionTablesData } = useCoachCommissionTables({
        centerId: authObject?.centerId || '',
        year: yearStr,
        month: monthStr2,
        enabled:
            isTierSettlement &&
            !!authObject?.centerId &&
            !salaryCalcData?.inScheduleBrackets &&
            !salaryCalcData?.outScheduleBrackets,
    });
    const fallbackBracket = React.useMemo(() => {
        const tables = commissionTablesData?.coachCommissionTables ?? [];
        const found = tables.find((t: any) => t.coachId === selectedCoach?.code);
        return {
            inScheduleBrackets: (found?.inScheduleBrackets ?? []) as SalaryCalcScheduleBracket[],
            outScheduleBrackets: (found?.outScheduleBrackets ?? []) as SalaryCalcScheduleBracket[],
        };
    }, [commissionTablesData, selectedCoach?.code]);
    const inScheduleBrackets: SalaryCalcScheduleBracket[] =
        salaryCalcData?.inScheduleBrackets ?? fallbackBracket.inScheduleBrackets;
    const outScheduleBrackets: SalaryCalcScheduleBracket[] =
        salaryCalcData?.outScheduleBrackets ?? fallbackBracket.outScheduleBrackets;
    // 매출 인센티브
    const [incentive, setIncentive] = React.useState(salaryCalcData?.incentive ?? 0);
    // 인센 타입
    const [incentiveType, setIncentiveType] = React.useState<CommissionType>(
        salaryCalcData?.incentiveType ?? 'percentage',
    );
    // 그룹 수업료 (토글 OFF 시 서버가 0 반환)
    const groupScheduleFee = salaryCalcData?.groupScheduleFee ?? 0;
    // 수업료(PT + 그룹 합산)는 아래 computeSalary 결과를 쓴다
    // IN/OUT 줄 건수·금액 — 신서버(상세 정산)는 구간표로만 계산한 수업만, 아니면 지금 그대로(서버 값 고르기만, tierInOutDisplay)
    const inOutDisplay = resolveInOutDisplay(salaryCalcData, settlementType);
    const inScheduleCount = inOutDisplay.inCount;
    const inScheduleCommissionRate = salaryCalcData?.inScheduleCommissionRate ?? 0;
    const totalInScheduleFee = inOutDisplay.inFee;
    const outScheduleCount = inOutDisplay.outCount;
    const outScheduleCommissionRate = salaryCalcData?.outScheduleCommissionRate ?? 0;
    const totalOutScheduleFee = inOutDisplay.outFee;
    const outAchievedBracketMinRevenue = salaryCalcData?.outAchievedBracketMinRevenue ?? 0;
    // 「시간 상관없이 IN」(노쇼 IN 설정·항상 IN 요율의 배수) — 서버 건수 고르기만(tierInOutDisplay). 기능을 안 쓰면 전부 0 → 화면 그대로.
    const forcedInNotes = resolveForcedInNotes(salaryCalcData, settlementType, inOutDisplay.isTierSplit);
    const inLineNoteText = inLineNote(forcedInNotes);

    // 요율 단위(원/%) — 판정은 features/salaryCalc/tierInOutDisplay 의 resolveCommissionUnit 하나.
    //  1) 급여 응답의 tierCommissionType / sessionCommissionType(그 강사 그 달 계산에 실제 쓴 값, 신서버)
    //  2) 상세 정산 구서버: 강사 요율표 조회의 그 강사 값(급여 계산과 같은 월 상속 조회). 조회 중·실패면 단정하지 않는다(null → 「-」).
    //  3) 세션별 정산 구서버: 강사별 방식을 읽을 조회가 없어 기존 % 표기 그대로.
    const serverTierCommissionType = salaryCalcData?.tierCommissionType;
    const { data: coachCommissionTablesData } = useCoachCommissionTables({
        centerId: authObject?.centerId || '',
        year: sessionOffsetYear,
        month: sessionOffsetMonth,
        enabled:
            settlementType === 'tier' &&
            !!authObject?.centerId &&
            serverTierCommissionType !== 'fixed' &&
            serverTierCommissionType !== 'percentage',
    });
    const matchedCoachTable = coachCommissionTablesData?.coachCommissionTables?.find(
        (c) => c.coachId === selectedCoachId,
    );
    const commissionUnit: CommissionUnit | null = resolveCommissionUnit({
        settlementType,
        data: salaryCalcData,
        tierTableCommissionType: matchedCoachTable?.commissionType,
    });

    /**
     * 적용 규칙별 PT 수업료 소계 — 서버 appliedRuleSummary 를 그대로 보인다(화면에서 다시 더하지 않는다).
     * 예외 규칙(분류·재등록)이 하나라도 있을 때만 보인다. 구간표뿐이면 지금 화면 그대로.
     * 상세 정산과 기본 정산(신서버는 예외 행이 있을 때만 준다)에서 같은 모양으로 보인다.
     * 세션별 정산은 회차 구간 표 아래 「예외 수업료」 한 줄로 따로 보인다(sessionExceptionScheduleCount).
     */
    const appliedRuleSummary =
        settlementType === 'tier' || settlementType === 'base' ? salaryCalcData?.appliedRuleSummary ?? [] : [];
    const sessionExceptionScheduleCount = isSessionTier ? salaryCalcData?.exceptionScheduleCount ?? 0 : 0;
    const sessionExceptionScheduleFee = salaryCalcData?.totalExceptionScheduleFee ?? 0;
    // 구서버: 분류·재등록 예외가 있을 때만(개별 요율만 쓰는 센터는 화면 그대로).
    // IN/OUT 이 구간표 수업만 보일 때: 「기본」 행을 빼고 예외·개별 요율 행만 — IN + OUT + 소계 = PT 수업료.
    const appliedRuleSummaryDisplay = resolveAppliedRuleSummaryDisplay(appliedRuleSummary, inOutDisplay.isTierSplit);
    const showAppliedRuleSummary = appliedRuleSummaryDisplay.show;
    /**
     * 구간 미설정 안내 — 판정은 features/salaryCalc/bracketNotice.ts 의 computeBracketMissing 하나(커밋 4b43791e 판정을 그대로 옮김).
     * 전체 강사 표의 「구간 미설정」 배지도 같은 함수를 쓴다.
     */
    const bracketMissingNotice = computeBracketMissing({
        data: salaryCalcData,
        settlementType,
        inScheduleBrackets,
        outScheduleBrackets,
    }).notice;
    const { data: settlementCategoriesState } = useSettlementCategories({
        centerId: authObject?.centerId || '',
        enabled: showAppliedRuleSummary && appliedRuleSummary.some((item) => item.appliedRule.startsWith('category:')),
    });
    const settlementCategoryMap = React.useMemo(
        () => buildCategoryNameMap(settlementCategoriesState?.categories),
        [settlementCategoriesState?.categories],
    );

    // 기본급
    const [baseSalary, setBaseSalary] = React.useState(salaryCalcData?.baseSalary ?? 0);
    // 강사명
    const coachName = selectedCoach?.value ?? '';
    // 강사 프로필
    const coachProfile = selectedCoach?.object?.profileImageUrl ?? undefined;

    // 기타, etcBonus, _id 제거
    const [etcBonus, setEtcBonus] = React.useState<
        {
            name: string;
            amount: number;
            amountType: EtcBonusAmountType;
            vatIncluded: boolean;
        }[]
    >(() =>
        (salaryCalcData?.etcBonus ?? []).map((bonus) => ({
            name: bonus.name,
            amount: bonus.amount,
            amountType: bonus.amountType ?? 'fixed',
            vatIncluded: bonus.vatIncluded ?? true,
        })),
    );
    // 3.3% 공제 여부
    const [isTaxable, setIsTaxable] = React.useState(false);
    // 급여 숫자 — 매출·인센티브·수업료·기타 수당·공제·최종 급여를 한 함수로 계산한다(전체 강사 표와 같은 식).
    // 이전에는 공제·최종 급여를 effect 로 state 에 넣었다. 같은 입력이면 같은 값이다(리팩터 동등성 테스트로 고정).
    const salaryCalc = computeSalary({
        data: salaryCalcData,
        edits: { incentive, incentiveType, baseSalary, etcBonus, isTaxable, revenueAdjustments },
        vatPolicy,
        vatRate,
    });
    const { settlement, autoSales, totalRevenueAdjustment, classFee, deduction, finalSalary } = salaryCalc;

    // 기타 수당 선택시 이름, 값
    const [selectedEtcBonus, setSelectedEtcBonus] = React.useState<
        | {
              name: string;
              amount: number;
              amountType: EtcBonusAmountType;
              vatIncluded: boolean;
          }
        | undefined
    >(undefined);

    const isPercentType = incentiveType === 'percentage';

    // 수업료 상세 펼침 여부
    const [isClassFeeExpanded, setIsClassFeeExpanded] = React.useState(true);
    // 인센티브 모달
    const [isIncentiveModal, setIsIncentiveModal] = React.useState(false);
    // 기타 수당 모달
    const [isEtcBonusModal, setIsEtcBonusModal] = React.useState(false);
    // 저장여부
    const [isSaved, setIsSaved] = React.useState(true);
    // 저장여부 경고 팝업
    const [isSaveWarning, setIsSaveWarning] = React.useState(false);

    const [navigateInfo, setNavigateInfo] = React.useState<NavigateInfo | null>(null);

    // 「강사별 / 전체 강사」 보기 — 기본은 강사별(기존 화면 그대로)
    const [salaryView, setSalaryView] = React.useState<SalaryView>('coach');
    const [tableAccessToken] = React.useState(() => getItemWithExpiration('authorization') ?? '');
    const tableCoaches = React.useMemo(() => salaryTableCoaches(coachList), [coachList]);
    // 표 조회는 강사별 화면과 같은 연·월 값(숫자 문자열, 예: '4')으로 같은 엔드포인트를 부른다
    const allCoachesSalary = useAllCoachesSalary({
        centerId: authObject?.centerId || '',
        accessToken: tableAccessToken,
        year: dayjs(date).year().toString(),
        month: (dayjs(date).month() + 1).toString(),
        coaches: tableCoaches,
        enabled: salaryView === 'all',
    });

    // 저장 진행 중 잠금. 진행 중에 들어온 저장은 무시한다(false) — 앞 요청 결과를 공유하면
    // 그사이 바뀐 값이 전송되지 않은 채 저장 완료로 처리되고 성공 처리가 클릭 수만큼 반복된다.
    const savingRef = React.useRef(false);
    const [isSaving, setIsSaving] = React.useState(false);

    // 저장 성공 시에만 true. 실패하면 사유를 알리고 false (호출부는 성공 처리·이동을 하지 않는다)
    const onSave = async (): Promise<boolean> => {
        if (savingRef.current) return false;
        savingRef.current = true;
        setIsSaving(true);
        // 저장 중 입력을 막는다 — 요청에 실리지 않은 편집이 성공 처리·새로고침에 덮여 사라지지 않게
        (document.activeElement as HTMLElement | null)?.blur?.();
        try {
            if (!Number.isFinite(incentive)) {
                alert('인센티브 값을 다시 확인해 주세요. 저장되지 않았어요.');
                return false;
            }
            let result: any;
            try {
                result = await postSalaryCalcsMutation.mutateAsync({
                    year: dayjs(date).year().toString(),
                    month: (dayjs(date).month() + 1).toString(),
                    incentive: incentive,
                    incentiveType: incentiveType,
                    baseSalary: baseSalary,
                    etcBonus: etcBonus,
                    isTaxable: isTaxable,
                    revenueAdjustments: revenueAdjustments,
                    selectedCoachId: selectedCoach?.code ?? '',
                });
            } catch (error) {
                result = { status: 0 };
            }
            const status = result?.status;
            if (status === 200) {
                setIsSaved(true);
                setIsRecalculatedNotice(false);
                // 전체 강사 표는 저장 전 결과를 들고 있으니 비운다 — 다음에 표를 열면 다시 부른다
                allCoachesSalary.invalidate();
                return true;
            }
            // 로그인 만료(401)·센터 열람 모드(403 VIEW_ONLY)는 전역 처리기가 이미 알린다
            if (status === 401 || result?.code === 'VIEW_ONLY' || result?.message === 'not logged in') {
                return false;
            }
            if (status === 400) {
                alert('입력값에 문제가 있어 저장되지 않았어요. 금액과 항목을 확인해 주세요.');
            } else if (status === 403) {
                alert('급여를 저장할 권한이 없어요.');
            } else if (status === 404) {
                alert('저장할 대상을 찾지 못해 저장되지 않았어요.');
            } else {
                if (status === 503 && result?.errorCode === SETTLEMENT_TRANSFER_LOOKUP_FAILED) {
                    // 서버는 저장 전에 양도 기록을 조회한다 — 이 실패면 저장되지 않았다.
                    alert('재등록 수업료 확인 중 일시적인 문제가 있었어요. 저장되지 않았어요. 잠시 후 다시 시도해 주세요.');
                    return false;
                }
                // 서버 오류·네트워크 오류는 서버가 이미 저장했을 수도 있다
                alert('저장 결과를 확인하지 못했어요. 새로고침 후 저장됐는지 확인해 주세요.');
            }
            return false;
        } finally {
            savingRef.current = false;
            setIsSaving(false);
        }
    };

    // 기타 수당 개별 환산 (percentage → 매출(settlement) × 비율 / 100, 부가세 기준액 floor 후 항목별 floor) — computeSalary 와 같은 함수
    const getEtcBonusKRW = (bonus: { amount: number; amountType?: EtcBonusAmountType; vatIncluded?: boolean }) =>
        etcBonusToKRW(bonus, settlement, vatPolicy, vatRate);

    /** 매출 조정은 저장 본문과 같은 세 필드로만 비교한다 — 조회 응답에는 하위문서 _id 가 붙어 와서, 통째로 비교하면 방금 저장한 값도 다르다고 본다(검증 P1). */
    const adjustmentFields = (list?: RevenueAdjustment[] | null) =>
        (list ?? []).map(({ reasonType, label, amount }) => ({ reasonType, label, amount }));

    /** 화면 편집값(기본급·인센티브·기타 수당·과세·매출 조정)이 이 서버 응답의 저장값과 다른가. */
    const editsDifferFrom = (data: SalaryCalcData) => {
        const initialEtcBonus = (data.etcBonus ?? []).map((bonus) => ({
            name: bonus.name,
            amount: bonus.amount,
            amountType: bonus.amountType ?? 'fixed',
            vatIncluded: bonus.vatIncluded ?? true,
        }));
        return (
            !isEqual(initialEtcBonus, etcBonus) ||
            !isEqual(adjustmentFields(data.revenueAdjustments), adjustmentFields(revenueAdjustments)) ||
            data.incentive !== incentive ||
            data.incentiveType !== incentiveType ||
            data.baseSalary !== baseSalary ||
            data.isTaxable !== isTaxable
        );
    };

    useEffect(() => {
        if (salaryCalcData) {
            setIsSaved(!editsDifferFrom(salaryCalcData));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incentive, incentiveType, baseSalary, etcBonus, isTaxable, salaryCalcData, revenueAdjustments]);

    // 화면 편집값을 서버 응답값으로 되돌린다(응답이 바뀔 때, 「저장 안 하고」 전체 강사 보기로 넘어갈 때)
    const resetEditsToServer = () => {
        if (salaryCalcData) {
            setIncentive(salaryCalcData.incentive);
            setIncentiveType(salaryCalcData.incentiveType);
            setBaseSalary(salaryCalcData.baseSalary);
            setEtcBonus(
                (salaryCalcData.etcBonus ?? []).map((bonus) => ({
                    name: bonus.name,
                    amount: bonus.amount,
                    amountType: bonus.amountType ?? 'fixed',
                    vatIncluded: bonus.vatIncluded ?? true,
                })),
            );
            setIsTaxable(salaryCalcData.isTaxable);
            // 매출 조정도 되돌린다 — 빠뜨리면 버린 조정이 남아 다음 저장에 실린다(검증 P1)
            setRevenueAdjustments(salaryCalcData.revenueAdjustments ?? []);
        }
    };

    /**
     * 같은 강사·같은 달 급여가 다시 조회됐는데(정산 설정 저장·예외 수업료 저장 등으로 수업료가 다시 계산됨) 저장 안 한 입력이 있으면
     * 입력을 서버 값으로 덮지 않는다 — 다시 조회된 응답은 수업료(계산값)만 바뀌고 입력값은 그대로라, 덮으면 입력이 말없이 사라진다.
     * 그때는 「수업료가 다시 계산됐어요」만 알린다. 강사·달이 바뀌었거나, 입력이 새 응답과 같거나(방금 저장), 입력이 없으면 지금처럼 되돌린다.
     */
    const lastServerRef = React.useRef<{ key: string; data: SalaryCalcData } | null>(null);
    const [isRecalculatedNotice, setIsRecalculatedNotice] = React.useState(false);
    useEffect(() => {
        if (!salaryCalcData) return;
        const key = `${selectedCoach?.code ?? ''}|${dayjs(date).format('YYYY-MM')}`;
        const prev = lastServerRef.current;
        lastServerRef.current = { key, data: salaryCalcData };
        if (
            prev &&
            prev.key === key &&
            prev.data !== salaryCalcData &&
            editsDifferFrom(prev.data) &&
            editsDifferFrom(salaryCalcData)
        ) {
            setIsRecalculatedNotice(true);
            return;
        }
        setIsRecalculatedNotice(false);
        resetEditsToServer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salaryCalcData]);

    const performNavigation = (info: NavigateInfo) => {
        if (info.type === 'month') {
            onMonthNavigate(info.payload);
        } else if (info.type === 'coach') {
            onCoachSelect(info.payload);
        } else if (info.type === 'view') {
            setSalaryView(info.payload);
        } else {
            // 전체 강사 표에서 강사 이름 — 그 강사의 강사별 보기로
            onCoachSelect(info.payload);
            setSalaryView('coach');
        }
    };

    const handleNavigation = (type: NavigateInfo['type'], payload: any) => {
        // 저장 중에는 이동·보기 전환을 막는다(늦게 끝난 저장이 다른 화면을 덮지 않게)
        if (savingRef.current) return;
        if (isSaved) {
            performNavigation({ type, payload });
        } else {
            setNavigateInfo({ type, payload });
            setIsSaveWarning(true);
        }
    };

    const allCoachesRows = React.useMemo(
        () =>
            tableCoaches.map((coach) => {
                const table = coachCommissionTablesData?.coachCommissionTables?.find((t: any) => t.coachId === coach.code);
                return buildAllCoachesRow({
                    coach,
                    outcome: allCoachesSalary.results[coach.code],
                    vatPolicy,
                    vatRate,
                    settlementType,
                    // 서버가 구간표를 안 줄 때 강사별 화면과 같은 폴백(요율표). 요율표를 아직 못 받았으면 구간 배지는 보류
                    fallbackBrackets: coachCommissionTablesData
                        ? {
                              inScheduleBrackets: (table?.inScheduleBrackets ?? []) as SalaryCalcScheduleBracket[],
                              outScheduleBrackets: (table?.outScheduleBrackets ?? []) as SalaryCalcScheduleBracket[],
                          }
                        : undefined,
                });
            }),
        [tableCoaches, allCoachesSalary.results, vatPolicy, vatRate, settlementType, coachCommissionTablesData],
    );
    const allCoachesTotals = React.useMemo(() => summarizeAllCoachesRows(allCoachesRows), [allCoachesRows]);

    return (
        <div className="flex flex-col w-full h-full pt-[29px] px-[46px] bg-white rounded-[27px] mb-[50px] pb-[50px]">
            {/* 저장 중에는 편집·월/강사 이동·모달 조작을 모두 막는다 */}
            {isSaving && <div className="fixed inset-0 z-[10000] cursor-wait" />}
            <Row className="items-center mb-[60px]">
                <MediumText fontSize={25} lineHeight={25} color={'#3D4244'} className="mr-[14px]">
                    {dayjs(date).format('YYYY년 MM월')}
                </MediumText>
                <div
                    className="flex cursor-pointer bg-[#F4F5F7] w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[5px]"
                    onClick={() => handleNavigation('month', dayjs(date).subtract(1, 'month'))}
                >
                    <SvgIcon
                        name={'SvgLeftStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                </div>

                <div
                    className="flex cursor-pointer bg-[#F4F5F7] w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[30px]"
                    onClick={() => handleNavigation('month', dayjs(date).add(1, 'month'))}
                >
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                </div>
                <SalaryViewToggle value={salaryView} onChange={(next) => handleNavigation('view', next)} />
                {salaryView === 'coach' && coachList && coachList.length > 0 && selectedCoach && (
                    <RealDropDown
                        initialSelected={selectedCoach.code}
                        options={coachList}
                        onSelect={(selected) => handleNavigation('coach', selected)}
                        bgColor="rgba(201, 219, 255, 0.4)"
                        textColor={colors.primary}
                        borderRadius={50}
                        totalText="모든 강사"
                        arrowColor={colors.primary}
                        className="py-[14px]"
                        textType="medium"
                        isAllOption={false}
                    />
                )}
            </Row>
            {salaryView === 'all' ? (
                <Col className="w-full px-[44px]">
                    <Col className="w-full mb-[16px] px-[14px] py-[10px] bg-[#F4F7FF] border border-solid border-[#D9E6FF] rounded-[8px]">
                        <RegularText fontSize={14} lineHeight={20} color="#3D4244" letterSpacing={-0.3} className="break-keep">
                            {`${salaryMonthLabel} 급여는 월 전체 수업(예정 포함) 기준입니다. 저장된 입력값(인센티브·기본급·수당·공제)과 지금 수업·매출로 계산한 값이에요. 강사별 화면에서 저장 전 고친 값은 들어가지 않아요.`}
                        </RegularText>
                        {vatPolicy === 'exclusive' && (
                            <RegularText fontSize={14} lineHeight={20} color="#8D6E00" letterSpacing={-0.3} className="mt-[4px]">
                                {`부가세 미포함 매출 기준 정산 중 (VAT ${vatRate}% 제외)`}
                            </RegularText>
                        )}
                    </Col>
                    <AllCoachesSalaryTable
                        rows={allCoachesRows}
                        totals={allCoachesTotals}
                        loadedCount={allCoachesSalary.loadedCount}
                        total={allCoachesSalary.total}
                        isLoading={allCoachesSalary.isLoading}
                        onSelectCoach={(coach) => handleNavigation('viewCoach', coach)}
                        onReload={allCoachesSalary.invalidate}
                        onExcel={() =>
                            exportAllCoachesSalaryExcel({
                                rows: allCoachesRows,
                                totals: allCoachesTotals,
                                monthLabel: salaryMonthLabel,
                                includeGroupClassInSettlement,
                                vatNote: vatPolicy === 'exclusive' ? `부가세 미포함 매출 기준 (VAT ${vatRate}% 제외)` : undefined,
                            })
                        }
                    />
                </Col>
            ) : loadErrorMessage ? (
                <SettlementLoadError message={loadErrorMessage} onRetry={refresh} />
            ) : (
            <Row className="flex w-full justify-between px-[44px] gap-[30px]">
                <Col className="flex justify-start min-w-0 flex-1 max-w-[820px]">
                    {vatPolicy === 'exclusive' && (
                        <Row className="flex items-center mb-[12px] px-[12px] py-[8px] bg-[#FFF8E1] border border-solid border-[#FFE082] rounded-[8px]">
                            <RegularText fontSize={14} lineHeight={20} color="#8D6E00" letterSpacing={-0.3}>
                                {`\u24D8 부가세 미포함 매출 기준 정산 중 (VAT ${vatRate}% 제외)`}
                            </RegularText>
                        </Row>
                    )}
                    <Col className="w-full mb-[12px] px-[14px] py-[10px] bg-[#F4F7FF] border border-solid border-[#D9E6FF] rounded-[8px]">
                        <Row className="items-start justify-between gap-[10px]">
                            <Row className="items-start min-w-0 flex-1">
                                <SvgIcon
                                    name="SvgInfoInCircle"
                                    size={16}
                                    fill={colors.primary}
                                    className="mr-[7px] mt-[2px] flex-shrink-0"
                                />
                                <RegularText
                                    fontSize={14}
                                    lineHeight={20}
                                    color="#3D4244"
                                    letterSpacing={-0.3}
                                    className="min-w-0 break-keep"
                                >
                                    {`${salaryMonthLabel} 급여는 월 전체 수업(예정 포함) 기준입니다.`}
                                </RegularText>
                            </Row>
                            <Row
                                className="items-center justify-center rounded-[20px] px-[9px] py-[4px] flex-shrink-0"
                                style={{ backgroundColor: includeGroupClassInSettlement ? '#E6F8F0' : '#F2F4F6' }}
                            >
                                <RegularText
                                    fontSize={13}
                                    lineHeight={18}
                                    color={includeGroupClassInSettlement ? '#067A46' : '#6B7280'}
                                    letterSpacing={-0.2}
                                >
                                    {groupSettlementLabel}
                                </RegularText>
                            </Row>
                        </Row>
                    </Col>
                    <Row className="items-center mb-[36px]">
                        {/* 매출액 */}
                        <Col className="w-[297px] bg-[#F6F8FA] border border-solid border-[#D9D9D9B2] rounded-[9px] py-[14px] px-[23px] mr-[20px]">
                            <Row className="justify-between items-center">
                                <RegularText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#3D4244">
                                    매출액
                                </RegularText>
                                <Row>
                                    <RegularText
                                        fontSize={17}
                                        lineHeight={24.64}
                                        letterSpacing={-0.32}
                                        color="#3D4244"
                                        className="mr-[5px]"
                                    >
                                        {convertToKRW(settlement)}
                                    </RegularText>
                                    <DemiLightText
                                        fontSize={17}
                                        lineHeight={24.64}
                                        letterSpacing={-0.32}
                                        color="#6B7280"
                                    >
                                        원
                                    </DemiLightText>
                                </Row>
                            </Row>
                            {revenueAdjustments.length > 0 && (
                                <Row className="justify-between items-center mt-[6px]">
                                    <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.3} color="#6B7280">
                                        자동 {convertToKRW(autoSales)}원 · 조정 {totalRevenueAdjustment > 0 ? '+' : ''}
                                        {convertToKRW(totalRevenueAdjustment)}원
                                    </DemiLightText>
                                </Row>
                            )}
                        </Col>
                        {/* 매출 조정 */}
                        <Row
                            className="border border-solid border-[#D9D9D9B2] rounded-[19.5px] bg-white py-[10px] px-[13px] items-center cursor-pointer mr-[8px]"
                            onClick={() => setIsRevenueAdjustmentModal(true)}
                        >
                            <SvgIcon name="SvgSetting" size={14} fill="#5A6370" className="mr-[6px]" />
                            <RegularText fontSize={13} lineHeight={18.82} letterSpacing={-0.32} color="#3D4244">
                                매출액 직접 조정
                            </RegularText>
                            {revenueAdjustments.length > 0 && (
                                <Col className="ml-[6px] bg-primary rounded-full w-[18px] h-[18px] items-center justify-center">
                                    <DemiLightText fontSize={13} lineHeight={18} color="white">
                                        {revenueAdjustments.length}
                                    </DemiLightText>
                                </Col>
                            )}
                        </Row>
                        {/* 인센티브 설정 */}
                        <Row
                            className="border border-solid border-[#E2E2E3] rounded-[19.5px] bg-[#434C54] py-[10px] px-[13px] items-center cursor-pointer"
                            onClick={() => setIsIncentiveModal(true)}
                        >
                            <SvgIcon name="SvgPercentage" size={17} fill={colors.white} className="mr-[7px]" />
                            <RegularText fontSize={13} lineHeight={18.82} letterSpacing={-0.32} color="white">
                                인센티브 설정
                            </RegularText>
                        </Row>
                    </Row>
                    <Row className="flex w-full bg-[#F6F8FA] border border-solid border-[#D9D9D9B2] rounded-[9px] py-[17.5px] px-[23px] justify-between mr-[20px] mb-[36px]">
                        <Row className="items-center">
                            <RegularText
                                fontSize={17}
                                lineHeight={24.64}
                                letterSpacing={-0.32}
                                color="#3D4244"
                                className="mr-[6px]"
                            >
                                매출 인센티브
                            </RegularText>
                            <TooltipIcon
                                title="매출액에 대한 인센티브 비율/금액을 적용하면 나타나는 금액"
                                fontSize={13}
                                lineHeight={16}
                                classNmae="px-[20px]"
                                fontType="demilight"
                            />
                            {isPercentType && (
                                <Row className=" items-center">
                                    <SvgIcon
                                        name="SvgPercentage"
                                        size={17}
                                        fill={colors.primary}
                                        className="ml-[14px] mr-[4px]"
                                    />
                                    <MediumText
                                        fontSize={15}
                                        lineHeight={21}
                                        letterSpacing={-0.32}
                                        color={colors.primary}
                                    >
                                        {`${incentive}%`}
                                    </MediumText>
                                </Row>
                            )}
                        </Row>
                        <Row className="items-center ">
                            {incentiveType ? (
                                <RegularText
                                    fontSize={17}
                                    lineHeight={24.64}
                                    letterSpacing={-0.32}
                                    color="#3D4244"
                                    className="mr-[11px]"
                                >
                                    {convertToKRW(salaryCalc.incentiveKRW)}
                                </RegularText>
                            ) : (
                                <Row className="items-center ">
                                    <SvgIcon name="SvgPercentage" size={17} fill={'#8D95A0'} className="mr-[4px]" />

                                    <RegularText
                                        fontSize={17}
                                        lineHeight={24.64}
                                        letterSpacing={-0.32}
                                        color="#6B7280"
                                        className="mr-[11px]"
                                    >
                                        인센티브를 설정해주세요
                                    </RegularText>
                                </Row>
                            )}
                            <DemiLightText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#6B7280">
                                원
                            </DemiLightText>
                        </Row>
                    </Row>
                    <Row className="flex w-full bg-[#F6F8FA] border border-solid border-[#D9D9D9B2] rounded-[9px] py-[17.5px] px-[23px] justify-between mr-[20px] mb-[36px]">
                        <RegularText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#3D4244">
                            수업료
                        </RegularText>
                        <Row>
                            <RegularText
                                fontSize={17}
                                lineHeight={24.64}
                                letterSpacing={-0.32}
                                color="#3D4244"
                                className="mr-[11px]"
                            >
                                {convertToKRW(classFee)}
                            </RegularText>
                            <DemiLightText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#6B7280">
                                원
                            </DemiLightText>
                        </Row>
                    </Row>
                    {/* 그룹 수업료 — classFee 안에 이미 합산되어 있으나 내역을 별도로 보여주는 정보 행 */}
                    {groupScheduleFee > 0 && (
                        <Row className="flex w-full bg-[#F6F8FA] border border-solid border-[#D9D9D9B2] rounded-[9px] py-[17.5px] px-[23px] justify-between mr-[20px] mb-[36px]">
                            <RegularText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#3D4244">
                                그룹 수업료
                            </RegularText>
                            <Row>
                                <RegularText
                                    fontSize={17}
                                    lineHeight={24.64}
                                    letterSpacing={-0.32}
                                    color="#3D4244"
                                    className="mr-[11px]"
                                >
                                    {convertToKRW(groupScheduleFee)}
                                </RegularText>
                                <DemiLightText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#6B7280">
                                    원
                                </DemiLightText>
                            </Row>
                        </Row>
                    )}
                    {/* 수업 수강권 매출액 — settlement 안에 이미 합산되어 있으나 내역을 별도로 보여주는 정보 행 */}
                    {groupSales > 0 && (
                        <Row className="flex w-full bg-[#F6F8FA] border border-solid border-[#D9D9D9B2] rounded-[9px] py-[17.5px] px-[23px] justify-between mr-[20px] mb-[36px]">
                            <RegularText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#3D4244">
                                수업 수강권 판매
                            </RegularText>
                            <Row>
                                <RegularText
                                    fontSize={17}
                                    lineHeight={24.64}
                                    letterSpacing={-0.32}
                                    color="#3D4244"
                                    className="mr-[11px]"
                                >
                                    {convertToKRW(groupSales)}
                                </RegularText>
                                <DemiLightText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#6B7280">
                                    원
                                </DemiLightText>
                            </Row>
                        </Row>
                    )}
                    <Row
                        className="w-full  border border-solid border-[#D9D9D9B2] rounded-[9px] py-[17.5px] px-[23px] justify-between mr-[20px] mb-[36px] cursor-text hover:border-primary transition-colors duration-300 ease-in-out"
                        onClick={() => {
                            bassicSalaryRef?.current?.focus();
                        }}
                    >
                        <RegularText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#3D4244">
                            기본급
                        </RegularText>
                        <Row>
                            <input
                                ref={bassicSalaryRef}
                                value={convertToKRW(baseSalary)}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    // , 없앰
                                    value = value.replace(/,/g, '');
                                    // 숫자만 정규식
                                    if (!/^[0-9]*$/.test(value)) {
                                        return;
                                    }
                                    // 0 일때 0지우고 숫자 입력
                                    if (value === '0') {
                                        setBaseSalary(Number(value));
                                        return;
                                    }

                                    setBaseSalary(Number(value));
                                }}
                                className="font-noto-regular text-[17px] leading-[24px] text-[#3D4244] bg-trans text-end mr-[11px]"
                                maxLength={15}
                                placeholder="금액을 입력해주세요"
                                onBlur={() => {}}
                            />
                            <DemiLightText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#6B7280">
                                원
                            </DemiLightText>
                        </Row>
                    </Row>
                    {etcBonus.map((bonus, index) => (
                        <AddContainer
                            key={index + bonus.name}
                            bonus={bonus}
                            convertedAmount={getEtcBonusKRW(bonus)}
                            index={index}
                            onClick={(_, bonus) => {
                                setSelectedEtcBonus(bonus);
                                setIsEtcBonusModal(true);
                            }}
                            onDelete={(index) => {
                                setEtcBonus((prev) => {
                                    const clone = cloneDeep(prev);
                                    const newBouns = clone.filter((_, i) => i !== index);
                                    return newBouns;
                                });
                            }}
                        />
                    ))}
                    {/* 기타 수당 추가하기 버튼 */}
                    <AddButton
                        // onClick={() => setEtcBonus([...etcBonus, { name: '', amount: 0 }])}
                        onClick={() => {
                            setSelectedEtcBonus(undefined);
                            setIsEtcBonusModal(true);
                        }}
                    />
                </Col>
                <Col className="w-[553px]  border border-solid border-[#D9DDE6] rounded-tl-[21px] rounded-tr-[21px] pt-[26px] pb-[40px] ">
                    <Row className=" items-center mb-[16px] px-[48px]">
                        <NewProfileImage
                            imageUrl={coachProfile}
                            size={41}
                            className="mr-[8px]"
                            imageFill="#D2D8DE"
                            iconSize={20}
                            circleColor="#F2F4F6"
                        />
                        <MediumText fontSize={22} lineHeight={32} letterSpacing={-0.32} color={colors.Text_1}>
                            {coachName + ' 강사'}
                        </MediumText>
                    </Row>
                    <Col
                        className="h-[1px] w-[457px] self-center
                     bg-[#E4E4E4] "
                    />
                    <Row className="py-[13px] justify-between items-center px-[48px]">
                        <Row className="  items-center ">
                            <SvgIcon name="SvgSettlementHistory" size={17} fill={colors.white} />
                            <RegularText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.32}
                                color={colors.Text_1}
                                className="ml-[8px] mr-[5px]"
                            >
                                {dayjs(date).format('M')}월 급여내역
                            </RegularText>
                        </Row>

                        <Row className="items-center" style={{ gap: 8 }}>
                            {isSessionTier && !!selectedCoachId && selectedCoachId !== 'all' && (
                                <Row
                                    className="items-center justify-center bg-[#EEF2FF] rounded-[7px] px-[15px] h-[33px] cursor-pointer"
                                    onClick={openSessionOffsetModal}
                                >
                                    <SvgIcon
                                        name={'SvgSettlementHistory'}
                                        size={14}
                                        fill={'#3D73DD'}
                                        className="mr-[6px]"
                                    />
                                    <RegularText fontSize={14} lineHeight={20} color="#3D73DD" letterSpacing={-0.32}>
                                        누적 회차 보정
                                    </RegularText>
                                </Row>
                            )}
                            <Row
                                className={` items-center justify-center bg-[#F2F4F6] rounded-[7px] px-[15px] h-[33px]  cursor-pointer

                            `}
                                onClick={() => {
                                    onClickExcel(
                                        buildCoachSalaryExcelValue({
                                            coachName,
                                            salary: salaryCalc,
                                            incentive,
                                            incentiveType,
                                            etcBonus,
                                            groupSales,
                                            groupScheduleFee,
                                        }),
                                        selectedCoach?.code ?? 'all',
                                    );
                                }}
                            >
                                <SvgIcon name={'SvgExcel'} size={16} fill={colors.trans} className="mr-[7px]" />
                                <RegularText fontSize={14} lineHeight={20} color="#3D4244" letterSpacing={-0.32}>
                                    엑셀 다운
                                </RegularText>
                            </Row>
                        </Row>
                    </Row>
                    <Col
                        className="h-[1px] w-[457px] self-center
                     bg-[#E4E4E4] "
                    />
                    {isRecalculatedNotice && (
                        <Row
                            role="status"
                            className="mx-[48px] mt-[14px] items-center rounded-[8px] bg-[#EEF4FF] px-[14px] py-[10px]"
                        >
                            <DemiLightText fontSize={13} letterSpacing={-0.45} color="#2B5DB3">
                                정산 설정이 바뀌어 수업료를 다시 계산했어요. 입력 중인 값은 그대로예요.
                            </DemiLightText>
                        </Row>
                    )}
                    {/* 급여내역, 스크롤 */}
                    <Col className="overflow-y-auto my-[30px] mr-[22px] scrollbar-thumb-[#B4B4B6] scrollbar-track-gray-200 scrollbar scrollbar-w-[5px]  scrollbar-track-rounded-full scrollbar-thumb-rounded-full scrollbar-h-[10px] ">
                        <Col className="min-h-[170px] pl-[48px] pr-[26px]">
                            <Row className=" justify-between mb-[12px]">
                                <Row>
                                    <DemiLightText
                                        fontSize={16}
                                        lineHeight={23}
                                        letterSpacing={-0.32}
                                        color={colors.Text_1}
                                    >
                                        매출 인센티브
                                    </DemiLightText>
                                    {isPercentType && (
                                        <Row className=" items-center">
                                            <SvgIcon
                                                name="SvgPercentage"
                                                size={17}
                                                fill={colors.primary}
                                                className="ml-[7px] mr-[4px]"
                                            />
                                            <MediumText
                                                fontSize={15}
                                                lineHeight={21}
                                                letterSpacing={-0.32}
                                                color={colors.primary}
                                            >
                                                {`${incentive}%`}
                                            </MediumText>
                                        </Row>
                                    )}
                                </Row>
                                <DemiLightText
                                    fontSize={16}
                                    lineHeight={23}
                                    letterSpacing={-0.32}
                                    color={colors.Text_1}
                                >
                                    {convertToKRW(salaryCalc.incentiveKRW) + ' 원'}
                                </DemiLightText>
                            </Row>
                            {/* 매출 구간 표 (tier 정산 전용) */}
                            {isTierSettlement && (
                                <RevenueBracketTable
                                    effectiveSales={settlement}
                                    inBrackets={inScheduleBrackets}
                                    outBrackets={outScheduleBrackets}
                                    commissionUnit={commissionUnit}
                                />
                            )}
                            {/* 수업료 - 접기/펼치기 */}
                            <Col className="mb-[12px]">
                                <Row
                                    className="justify-between items-center cursor-pointer"
                                    onClick={() => setIsClassFeeExpanded(!isClassFeeExpanded)}
                                >
                                    <DemiLightText
                                        fontSize={16}
                                        lineHeight={23}
                                        letterSpacing={-0.32}
                                        color={colors.Text_1}
                                    >
                                        수업료
                                    </DemiLightText>
                                    <Row className="items-center">
                                        <DemiLightText
                                            fontSize={16}
                                            lineHeight={23}
                                            letterSpacing={-0.32}
                                            color={colors.Text_1}
                                            className="mr-[8px]"
                                        >
                                            {convertToKRW(classFee)} 원
                                        </DemiLightText>
                                        <SvgIcon
                                            name={isClassFeeExpanded ? 'SvgUpStrokeArrow' : 'SvgDownStrokeArrow'}
                                            size={12}
                                            fill={colors.trans}
                                            stroke="#71757C"
                                            strokeWidth={2}
                                        />
                                    </Row>
                                </Row>
                                {bracketMissingNotice && (
                                    <div
                                        role="note"
                                        aria-label="수업료 구간 안내"
                                        className="mt-[8px] flex flex-wrap items-center gap-x-[6px] rounded-[8px] bg-[#FFF6E5] px-[12px] py-[8px] text-[14px] leading-[20px] text-[#8A5A00]"
                                    >
                                        <span>{bracketMissingNotice}</span>
                                        {onOpenCommissionSetting && (
                                            <>
                                                <span aria-hidden="true">·</span>
                                                <button
                                                    type="button"
                                                    onClick={onOpenCommissionSetting}
                                                    className="font-medium text-[#2B5DB3] underline underline-offset-2"
                                                >
                                                    설정하기
                                                </button>
                                            </>
                                        )}
                                    </div>
                                )}
                                {isClassFeeExpanded && !isSessionTier && (
                                    <Col className="flex flex-row mt-[12px] ml-[4px] mb-[20px]">
                                        <Row className="flex">
                                            <Col className="flex w-[3px] bg-[#E6E8EB] h-full mr-[15px] rounded-full" />
                                            <Col className="flex w-full">
                                                {/* 근무 시간 내 수업 */}
                                                <Row className="items-center justify-between py-[8px]">
                                                    <Row className="items-center">
                                                        <DemiLightText
                                                            fontSize={15}
                                                            letterSpacing={-0.45}
                                                            color={colors.Text_1}
                                                            className="mr-[4px]"
                                                        >
                                                            {inLineTitle(forcedInNotes)}
                                                        </DemiLightText>
                                                        <div className="bg-[#E6F2EC] rounded-full w-[38px] h-[22px] mr-[15px] flex items-center justify-center">
                                                            <MediumText fontSize={13} lineHeight={18} color="#12A25D">
                                                                IN
                                                            </MediumText>
                                                        </div>
                                                        <DemiLightText
                                                            fontSize={14}
                                                            letterSpacing={-0.45}
                                                            color="#6B7280"
                                                            className="mr-[4px]"
                                                        >
                                                            {inScheduleCount}건
                                                        </DemiLightText>
                                                        {commissionUnit === 'percentage' && (
                                                            <SvgIcon
                                                                name="SvgPercentage"
                                                                size={13}
                                                                fill="#12A25D"
                                                                className="mr-[2px]"
                                                            />
                                                        )}
                                                        <DemiLightText
                                                            fontSize={13}
                                                            letterSpacing={-0.45}
                                                            color="#12A25D"
                                                        >
                                                            {formatCommissionRateText(
                                                                inScheduleCommissionRate,
                                                                commissionUnit,
                                                            )}
                                                        </DemiLightText>
                                                    </Row>
                                                    <DemiLightText
                                                        fontSize={16}
                                                        letterSpacing={-0.45}
                                                        color={colors.Text_1}
                                                    >
                                                        {convertToKRW(totalInScheduleFee)} 원
                                                    </DemiLightText>
                                                </Row>
                                                {inLineNoteText && (
                                                    <DemiLightText
                                                        fontSize={13}
                                                        letterSpacing={-0.45}
                                                        color="#6B7280"
                                                        className="mt-[-6px] mb-[2px]"
                                                    >
                                                        {inLineNoteText}
                                                    </DemiLightText>
                                                )}
                                                {/* 근무 시간 외 수업 */}
                                                <Row className="items-center justify-between py-[8px]">
                                                    <Row className="items-center">
                                                        <DemiLightText
                                                            fontSize={15}
                                                            letterSpacing={-0.45}
                                                            color={colors.Text_1}
                                                            className="mr-[4px]"
                                                        >
                                                            근무 시간 외 수업
                                                        </DemiLightText>
                                                        <div className="bg-[#FDECEA] rounded-full w-[49px] h-[22px] mr-[15px] flex items-center justify-center">
                                                            <MediumText fontSize={13} lineHeight={18} color="#E57373">
                                                                OUT
                                                            </MediumText>
                                                        </div>
                                                        <DemiLightText
                                                            fontSize={14}
                                                            letterSpacing={-0.45}
                                                            color="#6B7280"
                                                            className="mr-[4px]"
                                                        >
                                                            {outScheduleCount}건
                                                        </DemiLightText>
                                                        {commissionUnit === 'percentage' && (
                                                            <SvgIcon
                                                                name="SvgPercentage"
                                                                size={13}
                                                                fill="#E57373"
                                                                className="mr-[2px]"
                                                            />
                                                        )}
                                                        <DemiLightText
                                                            fontSize={13}
                                                            letterSpacing={-0.45}
                                                            color="#E57373"
                                                        >
                                                            {formatCommissionRateText(
                                                                outScheduleCommissionRate,
                                                                commissionUnit,
                                                            )}
                                                        </DemiLightText>
                                                    </Row>
                                                    <DemiLightText
                                                        fontSize={16}
                                                        letterSpacing={-0.45}
                                                        color={colors.Text_1}
                                                    >
                                                        {convertToKRW(totalOutScheduleFee)} 원
                                                    </DemiLightText>
                                                </Row>
                                                {groupScheduleFee > 0 && (
                                                    <Row className="items-center justify-between py-[8px]">
                                                        <Row className="items-center">
                                                            <DemiLightText
                                                                fontSize={15}
                                                                letterSpacing={-0.45}
                                                                color={colors.Text_1}
                                                                className="mr-[4px]"
                                                            >
                                                                그룹 수업
                                                            </DemiLightText>
                                                            <div className="bg-[#EEF2FF] rounded-full w-[49px] h-[22px] mr-[15px] flex items-center justify-center">
                                                                <MediumText
                                                                    fontSize={13}
                                                                    lineHeight={18}
                                                                    color={colors.primary}
                                                                >
                                                                    그룹
                                                                </MediumText>
                                                            </div>
                                                        </Row>
                                                        <DemiLightText
                                                            fontSize={16}
                                                            letterSpacing={-0.45}
                                                            color={colors.Text_1}
                                                        >
                                                            {convertToKRW(groupScheduleFee)} 원
                                                        </DemiLightText>
                                                    </Row>
                                                )}
                                                {groupScheduleFee > 0 && forcedInNotes.groupNoshowCount > 0 && (
                                                    <DemiLightText
                                                        fontSize={13}
                                                        letterSpacing={-0.45}
                                                        color="#6B7280"
                                                        className="mt-[-6px] mb-[2px]"
                                                    >
                                                        {`근무 시간 밖 노쇼 ${forcedInNotes.groupNoshowCount}건 IN 포함`}
                                                    </DemiLightText>
                                                )}
                                                {forcedInNotes.zeroRateCount > 0 && (
                                                    <Row
                                                        role="note"
                                                        className="items-center bg-[#FFF8E1] rounded-[8px] px-[14px] py-[10px] mt-[6px] gap-[6px]"
                                                    >
                                                        <SvgIcon name="SvgWarningInCircle" size={14} fill="#F59E0B" />
                                                        <DemiLightText fontSize={13} letterSpacing={-0.45} color="#92400E">
                                                            {`IN 요율이 0이라 0원이 된 수업이 ${forcedInNotes.zeroRateCount}건 있어요.`}
                                                        </DemiLightText>
                                                    </Row>
                                                )}
                                                {/* 안내 메시지 */}
                                                {outAchievedBracketMinRevenue !== 0 && (
                                                    <Row className="items-center bg-[#F6F8FA] rounded-[8px] px-[14px] py-[10px] mt-[6px]">
                                                        <Col className="w-[14px] h-[14px] rounded-full bg-[#848F9A] flex items-center justify-center mr-[6px]">
                                                            <SvgIcon
                                                                name="SvgCheck"
                                                                size={8}
                                                                fill={colors.trans}
                                                                stroke="white"
                                                                strokeWidth={2}
                                                            />
                                                        </Col>
                                                        <DemiLightText
                                                            fontSize={13}
                                                            letterSpacing={-0.45}
                                                            color={colors.Text_1}
                                                        >
                                                            {commissionUnit
                                                                ? `당월 누적 매출 ${convertToKRW(outAchievedBracketMinRevenue)}만원 달성으로 근무 시간 외 수업 ${formatCommissionRateText(outScheduleCommissionRate, commissionUnit)} 요율 적용`
                                                                : `당월 누적 매출 ${convertToKRW(outAchievedBracketMinRevenue)}만원 달성으로 근무 시간 외 수업 구간 요율 적용`}
                                                        </DemiLightText>
                                                    </Row>
                                                )}
                                                {showAppliedRuleSummary && (
                                                    <Col
                                                        aria-label={appliedRuleSummaryDisplay.title}
                                                        className="mt-[10px] rounded-[8px] border border-solid border-[#E6E8EB] px-[12px] py-[8px]"
                                                    >
                                                        <DemiLightText
                                                            fontSize={13}
                                                            letterSpacing={-0.45}
                                                            color="#6B7280"
                                                            className="mb-[2px]"
                                                        >
                                                            {appliedRuleSummaryDisplay.title}
                                                        </DemiLightText>
                                                        {appliedRuleSummaryDisplay.rows.map((item) => (
                                                            <Row
                                                                key={item.appliedRule}
                                                                className="items-center justify-between py-[3px]"
                                                            >
                                                                <DemiLightText
                                                                    fontSize={13}
                                                                    letterSpacing={-0.45}
                                                                    color={colors.Text_1}
                                                                >
                                                                    {`${
                                                                        formatAppliedRuleSummaryLabel(
                                                                            item,
                                                                            settlementCategoryMap,
                                                                        ) ?? '-'
                                                                    } · ${item.scheduleCount}건`}
                                                                </DemiLightText>
                                                                <DemiLightText
                                                                    fontSize={13}
                                                                    letterSpacing={-0.45}
                                                                    color={colors.Text_1}
                                                                >
                                                                    {convertToKRW(item.totalScheduleFee)} 원
                                                                </DemiLightText>
                                                            </Row>
                                                        ))}
                                                    </Col>
                                                )}
                                            </Col>
                                        </Row>
                                    </Col>
                                )}
                                {isClassFeeExpanded && isSessionTier && (
                                    <Col className="flex flex-row mt-[12px] ml-[4px] mb-[20px]">
                                        <Row className="flex w-full">
                                            <Col className="flex w-[3px] bg-[#E6E8EB] h-full mr-[15px] rounded-full" />
                                            <Col className="flex w-full">
                                                <Row className="items-center justify-between py-[8px]">
                                                    <DemiLightText
                                                        fontSize={15}
                                                        letterSpacing={-0.45}
                                                        color={colors.Text_1}
                                                    >
                                                        이번 달 총 수업 회차
                                                    </DemiLightText>
                                                    <DemiLightText
                                                        fontSize={16}
                                                        letterSpacing={-0.45}
                                                        color={colors.Text_1}
                                                    >
                                                        {totalSessionCount}회
                                                    </DemiLightText>
                                                </Row>
                                                {(bracketBreakdown.length > 0 || sessionExceptionScheduleCount > 0) && (
                                                    <Col className="mt-[4px] rounded-[8px] border border-solid border-[#E6E8EB]">
                                                        <Row className="px-[12px] py-[8px] bg-[#F6F8FA] rounded-t-[8px]">
                                                            <div className="flex-1">
                                                                <DemiLightText
                                                                    fontSize={13}
                                                                    letterSpacing={-0.45}
                                                                    color="#6B7280"
                                                                >
                                                                    구간
                                                                </DemiLightText>
                                                            </div>
                                                            <div className="w-[60px] text-right">
                                                                <DemiLightText
                                                                    fontSize={13}
                                                                    letterSpacing={-0.45}
                                                                    color="#6B7280"
                                                                >
                                                                    요율
                                                                </DemiLightText>
                                                            </div>
                                                            <div className="w-[60px] text-right">
                                                                <DemiLightText
                                                                    fontSize={13}
                                                                    letterSpacing={-0.45}
                                                                    color="#6B7280"
                                                                >
                                                                    수업수
                                                                </DemiLightText>
                                                            </div>
                                                            <div className="w-[100px] text-right">
                                                                <DemiLightText
                                                                    fontSize={13}
                                                                    letterSpacing={-0.45}
                                                                    color="#6B7280"
                                                                >
                                                                    수업료 합계
                                                                </DemiLightText>
                                                            </div>
                                                        </Row>
                                                        {bracketBreakdown.map((bracket, index) => (
                                                            <Row
                                                                key={`${bracket.minSessions}-${
                                                                    bracket.maxSessions ?? 'max'
                                                                }-${index}`}
                                                                className="px-[12px] py-[8px] items-center border-t border-solid border-[#E6E8EB]"
                                                            >
                                                                <div className="flex-1">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color={colors.Text_1}
                                                                    >
                                                                        {bracket.maxSessions === null ||
                                                                        bracket.maxSessions === undefined
                                                                            ? `${bracket.minSessions}회 이상`
                                                                            : `${bracket.minSessions}~${bracket.maxSessions}회`}
                                                                    </DemiLightText>
                                                                </div>
                                                                <div className="w-[60px] text-right">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color={colors.primary}
                                                                    >
                                                                        {formatCommissionRateText(
                                                                            bracket.commissionRate,
                                                                            commissionUnit,
                                                                        )}
                                                                    </DemiLightText>
                                                                </div>
                                                                <div className="w-[60px] text-right">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color={colors.Text_1}
                                                                    >
                                                                        {bracket.sessionCount}건
                                                                    </DemiLightText>
                                                                </div>
                                                                <div className="w-[100px] text-right">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color={colors.Text_1}
                                                                    >
                                                                        {convertToKRW(bracket.totalFee)} 원
                                                                    </DemiLightText>
                                                                </div>
                                                            </Row>
                                                        ))}
                                                        {sessionExceptionScheduleCount > 0 && (
                                                            // 예외 수업은 회차 구간 소계에서 빠진다(서버) — 서버가 준 건수·금액을 한 줄로 보인다.
                                                            <Row
                                                                aria-label="예외 수업료 소계"
                                                                className="px-[12px] py-[8px] items-center border-t border-solid border-[#E6E8EB]"
                                                            >
                                                                <div className="flex-1">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color={colors.Text_1}
                                                                    >
                                                                        예외 수업료
                                                                    </DemiLightText>
                                                                </div>
                                                                <div className="w-[60px] text-right">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color="#6B7280"
                                                                    >
                                                                        -
                                                                    </DemiLightText>
                                                                </div>
                                                                <div className="w-[60px] text-right">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color={colors.Text_1}
                                                                    >
                                                                        {sessionExceptionScheduleCount}건
                                                                    </DemiLightText>
                                                                </div>
                                                                <div className="w-[100px] text-right">
                                                                    <DemiLightText
                                                                        fontSize={14}
                                                                        letterSpacing={-0.45}
                                                                        color={colors.Text_1}
                                                                    >
                                                                        {convertToKRW(sessionExceptionScheduleFee)} 원
                                                                    </DemiLightText>
                                                                </div>
                                                            </Row>
                                                        )}
                                                    </Col>
                                                )}
                                                <DemiLightText
                                                    fontSize={13}
                                                    letterSpacing={-0.45}
                                                    color="#6B7280"
                                                    className="mt-[8px]"
                                                >
                                                    구간은 라포 서비스에 등록된 누적 수업 회차 기준으로 적용됩니다.
                                                </DemiLightText>
                                            </Col>
                                        </Row>
                                    </Col>
                                )}
                            </Col>
                            <Row className=" justify-between mb-[12px]">
                                <DemiLightText
                                    fontSize={16}
                                    lineHeight={23}
                                    letterSpacing={-0.32}
                                    color={colors.Text_1}
                                >
                                    기본급
                                </DemiLightText>
                                <DemiLightText
                                    fontSize={16}
                                    lineHeight={23}
                                    letterSpacing={-0.32}
                                    color={colors.Text_1}
                                >
                                    {convertToKRW(baseSalary)} 원
                                </DemiLightText>
                            </Row>
                            {etcBonus.map((bonus, index) => (
                                <Row className=" justify-between mb-[12px]" key={index + bonus.name}>
                                    <Row className="items-center">
                                        <DemiLightText
                                            fontSize={16}
                                            lineHeight={23}
                                            letterSpacing={-0.32}
                                            color={colors.Text_1}
                                        >
                                            {bonus.name}
                                        </DemiLightText>
                                        {bonus.amountType === 'percentage' && (
                                            <DemiLightText
                                                fontSize={13}
                                                lineHeight={18}
                                                letterSpacing={-0.3}
                                                color="#6B7280"
                                                className="ml-[6px]"
                                            >
                                                {`(센터 매출의 ${bonus.amount}% · ${
                                                    bonus.vatIncluded === false ? 'VAT 제외' : 'VAT 포함'
                                                })`}
                                            </DemiLightText>
                                        )}
                                    </Row>
                                    <DemiLightText
                                        fontSize={16}
                                        lineHeight={23}
                                        letterSpacing={-0.32}
                                        color={colors.Text_1}
                                    >
                                        {convertToKRW(getEtcBonusKRW(bonus))} 원
                                    </DemiLightText>
                                </Row>
                            ))}
                            {isTaxable && (
                                <Row className=" justify-between mb-[12px]">
                                    <DemiLightText
                                        fontSize={16}
                                        lineHeight={23}
                                        letterSpacing={-0.32}
                                        color={colors.Text_1}
                                    >
                                        3.3% 공제
                                    </DemiLightText>
                                    <DemiLightText
                                        fontSize={16}
                                        lineHeight={23}
                                        letterSpacing={-0.32}
                                        color={colors.Text_1}
                                    >
                                        {'-' + convertToKRW(deduction) + ' 원'}
                                    </DemiLightText>
                                </Row>
                            )}
                        </Col>
                    </Col>
                    <Col className="h-[1px]  bg-[#E4E4E4] mb-[52px] w-[390px] self-center" />
                    <Row className=" items-center justify-between mb-[13px] px-[48px]">
                        <MediumText fontSize={18} lineHeight={26} letterSpacing={-0.32} color={colors.primary}>
                            최종 급여액
                        </MediumText>
                        <MediumText fontSize={26} lineHeight={37} letterSpacing={-0.32} color={colors.primary}>
                            {convertToKRW(finalSalary)}원
                        </MediumText>
                    </Row>
                    <Row className=" self-end pr-[48px] mb-[30px]">
                        <label className={`relative inline-flex items-center cursor-pointer mr-[8px] `}>
                            <div
                                onClick={() => {
                                    setIsTaxable(!isTaxable);
                                }}
                                className={`w-[16px] h-[16px] border-[1px] border-[#D2D8DE] border-solid rounded-md flex items-center justify-center 
                                    ${isTaxable ? 'bg-[#3D4244]' : 'bg-trans'}
                                    `}
                                style={{
                                    width: 18,
                                    height: 18,
                                }}
                            >
                                {isTaxable ? (
                                    <svg
                                        className=" text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            xmlns="http://www.w3.org/2000/svg"
                                            d="M5 11L10.6667 16.6666L20 6"
                                            stroke="white"
                                            strokeWidth={1.5}
                                        />
                                    </svg>
                                ) : (
                                    <div className="w-[15px] h-[15px]" />
                                )}
                            </div>
                        </label>
                        <DemiLightText fontSize={14} lineHeight={20} letterSpacing={-0.32} color={colors.Text_1}>
                            3.3% 공제
                        </DemiLightText>
                    </Row>
                    <Col
                        className={`w-[398px] h-[60px] bg-[#2A2B2C] rounded-[15px] flex justify-center items-center self-center ${
                            isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={() => {
                            onSave().then((saved) => {
                                if (!saved) return;
                                alert('급여 내역이 저장되었습니다.');
                                refresh();
                            });
                        }}
                    >
                        <RegularText fontSize={20} lineHeight={24} color={'white'}>
                            {isSaving ? '저장 중...' : '급여 내역 저장'}
                        </RegularText>
                    </Col>
                </Col>
            </Row>
            )}
            {isIncentiveModal && (
                <ModalSalaryCalIncentive
                    setIsVisible={setIsIncentiveModal}
                    incentive={incentive.toString()}
                    type={incentiveType}
                    onClickConfirm={(incen, incenType) => {
                        setIncentive(Number.parseInt(incen));
                        setIncentiveType(incenType);
                    }}
                />
            )}
            {isRevenueAdjustmentModal && (
                <ModalRevenueAdjustment
                    setIsVisible={setIsRevenueAdjustmentModal}
                    autoSales={autoSales}
                    refundAmount={refundAmount}
                    initialAdjustments={revenueAdjustments}
                    onSave={(next) => {
                        setRevenueAdjustments(next);
                        setIsSaved(false);
                    }}
                />
            )}
            {isEtcBonusModal && (
                <ModalAddEtcSalary
                    setIsVisible={setIsEtcBonusModal}
                    selectedEtcBonus={selectedEtcBonus}
                    settlement={settlement}
                    vatPolicy={vatPolicy}
                    vatRate={vatRate}
                    onClickConfirm={(value, type) => {
                        const clone = cloneDeep(etcBonus);

                        if (type === 'new') {
                            clone.push(value);
                        } else {
                            const index = clone.findIndex((bonus) => bonus.name === value.name);
                            if (index >= 0) {
                                clone[index] = value;
                            } else {
                                clone.push(value);
                            }
                        }
                        setEtcBonus(clone);
                    }}
                />
            )}
            {isSaveWarning && (
                <ModalNoSaveChangeVariable
                    setIsVisible={(visible) => {
                        // 저장 진행 중에는 Esc 등으로 닫히지 않게 한다(늦게 끝난 저장이 이동시키지 않도록)
                        if (savingRef.current) return;
                        setIsSaveWarning(visible);
                    }}
                    onClose={() => {
                        if (savingRef.current) return;
                        setIsSaveWarning(false);
                    }}
                    onDontSave={() => {
                        setIsSaveWarning(false);
                        if (navigateInfo) {
                            // 보기 전환은 응답이 그대로라 편집값이 남는다 — 버린 편집을 서버 값으로 되돌린다
                            if (navigateInfo.type === 'view') resetEditsToServer();
                            performNavigation(navigateInfo);
                        }
                        refresh();
                    }}
                    onSave={() => {
                        onSave().then((saved) => {
                            if (!saved) return;
                            setIsSaveWarning(false);
                            if (navigateInfo) {
                                performNavigation(navigateInfo);
                            }
                            refresh();
                        });
                    }}
                />
            )}
        </div>
    );
};

export default SalaryCalculationPage;

const AddContainer = ({
    bonus,
    convertedAmount,
    index,
    onClick,
    onDelete,
}: {
    bonus: {
        name: string;
        amount: number;
        amountType?: EtcBonusAmountType;
        vatIncluded?: boolean;
    };
    convertedAmount: number;
    index: number;
    onClick?: (
        index: number,
        bonus: {
            name: string;
            amount: number;
            amountType: EtcBonusAmountType;
            vatIncluded: boolean;
        },
    ) => void;
    onDelete?: (index: number) => void;
}) => {
    const isPercentage = bonus.amountType === 'percentage';
    const resolvedType: EtcBonusAmountType = bonus.amountType ?? 'fixed';
    const resolvedVatIncluded = bonus.vatIncluded ?? true;
    return (
        <Row className="w-full items-center relative  mb-[36px]">
            <Row
                key={index + bonus.name}
                className="w-full  border border-solid border-[#D9D9D9B2] rounded-[9px] py-[17.5px] px-[23px] justify-between   hover:border-primary transition-colors duration-300 ease-in-out cursor-pointer"
                onClick={() =>
                    onClick &&
                    onClick(index, {
                        name: bonus.name,
                        amount: bonus.amount,
                        amountType: resolvedType,
                        vatIncluded: resolvedVatIncluded,
                    })
                }
            >
                <Row className="items-center">
                    <RegularText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#3D4244">
                        {bonus.name}
                    </RegularText>
                    {isPercentage && (
                        <DemiLightText
                            fontSize={13}
                            lineHeight={18}
                            letterSpacing={-0.3}
                            color="#6B7280"
                            className="ml-[8px]"
                        >
                            {`센터 매출의 ${bonus.amount}% · ${resolvedVatIncluded ? 'VAT 포함' : 'VAT 제외'}`}
                        </DemiLightText>
                    )}
                </Row>
                <Row>
                    <RegularText
                        fontSize={17}
                        lineHeight={24.64}
                        letterSpacing={-0.32}
                        color="#3D4244"
                        className="mr-[11px]"
                    >
                        {convertToKRW(convertedAmount)}
                    </RegularText>
                    <DemiLightText fontSize={17} lineHeight={24.64} letterSpacing={-0.32} color="#6B7280">
                        원
                    </DemiLightText>
                </Row>
            </Row>
            <SvgIcon
                name="SvgCircleX"
                size={17}
                fill={'#434C54'}
                className=" absolute right-[-33px] cursor-pointer "
                onClick={() => onDelete && onDelete(index)}
            />
        </Row>
    );
};

// 매출 구간 표 (tier 정산 전용)
//  - 매출(원) 을 만원 단위로 환산해 [minRevenue, maxRevenue) 범위에 속하는 구간을 하이라이트.
//  - 마지막 구간(maxRevenue === null)은 minRevenue 이상이면 매칭.
//  - 근무 시간 내/외 표를 각각 독립적으로 접고 펼친다 (디폴트 접힘).
const RevenueBracketTable = ({
    effectiveSales,
    inBrackets,
    outBrackets,
    commissionUnit,
}: {
    effectiveSales: number;
    inBrackets: SalaryCalcScheduleBracket[];
    outBrackets: SalaryCalcScheduleBracket[];
    /** 요율 단위 — 수업료 IN/OUT 줄과 같은 판정. null 이면 모름(「-」) */
    commissionUnit: CommissionUnit | null;
}) => {
    const salesInManwon = effectiveSales / 10000;
    // 음수 매출(환불이 매출보다 큰 경우) — 백엔드도 첫 구간을 적용해 요율을 계산하므로
    // 프론트 표시도 첫 구간을 active 로 본다.
    const findActiveIndex = (brackets: SalaryCalcScheduleBracket[]) => {
        if (brackets.length === 0) return -1;
        if (salesInManwon < 0) return 0;
        return brackets.findIndex(
            (b) => salesInManwon >= b.minRevenue && (b.maxRevenue === null || salesInManwon < b.maxRevenue),
        );
    };

    return (
        <Col className="mb-[18px] mt-[6px]">
            <Row className="justify-between items-center mb-[10px]">
                <DemiLightText fontSize={14} lineHeight={20} letterSpacing={-0.3} color="#5A6370">
                    매출 구간에 따른 수업료
                </DemiLightText>
                <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.3} color="#6B7280">
                    현재 매출 {convertToKRW(effectiveSales)}원
                </DemiLightText>
            </Row>
            <CollapsibleBracketCard
                title="근무 시간 내 수업"
                badge={{ label: 'IN', bg: '#E6F2EC', color: '#12A25D' }}
                brackets={inBrackets}
                activeIdx={findActiveIndex(inBrackets)}
                commissionUnit={commissionUnit}
            />
            <CollapsibleBracketCard
                title="근무 시간 외 수업"
                badge={{ label: 'OUT', bg: '#FDECEA', color: '#E57373' }}
                brackets={outBrackets}
                activeIdx={findActiveIndex(outBrackets)}
                commissionUnit={commissionUnit}
            />
        </Col>
    );
};

// 단일 구간 카드 — 자체적으로 접기/펼치기 상태 관리
//  접힘: 헤더(뱃지 + 타이틀) + 현재 구간 요약("X ~ Y만원 · Z%")
//  펼침: 전체 매출 구간 표
const CollapsibleBracketCard = ({
    title,
    badge,
    brackets,
    activeIdx,
    commissionUnit,
}: {
    title: string;
    badge: { label: string; bg: string; color: string };
    brackets: SalaryCalcScheduleBracket[];
    activeIdx: number;
    commissionUnit: CommissionUnit | null;
}) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const active = activeIdx >= 0 ? brackets[activeIdx] : undefined;

    const renderRangeText = (b: SalaryCalcScheduleBracket) =>
        b.maxRevenue === null
            ? `${b.minRevenue.toLocaleString()}만원 이상`
            : `${b.minRevenue.toLocaleString()} ~ ${b.maxRevenue.toLocaleString()}만원`;

    return (
        <Col className="mb-[10px] rounded-[10px] border border-solid border-[#E6E8EB] overflow-hidden">
            {/* 헤더 — 클릭으로 토글 */}
            <Row
                className="px-[14px] py-[12px] items-center justify-between cursor-pointer"
                onClick={() => setIsExpanded((v) => !v)}
            >
                <Row className="items-center">
                    <div
                        className="rounded-full px-[8px] h-[20px] mr-[10px] flex items-center justify-center"
                        style={{ backgroundColor: badge.bg }}
                    >
                        <MediumText fontSize={13} lineHeight={18} color={badge.color}>
                            {badge.label}
                        </MediumText>
                    </div>
                    <DemiLightText fontSize={14} lineHeight={20} letterSpacing={-0.3} color={colors.Text_1}>
                        {title}
                    </DemiLightText>
                </Row>
                <Row className="items-center">
                    {!isExpanded && (
                        <Row className="items-center mr-[10px]">
                            {active ? (
                                <>
                                    <DemiLightText
                                        fontSize={13}
                                        lineHeight={18}
                                        letterSpacing={-0.3}
                                        color={colors.Text_1}
                                        className="mr-[8px]"
                                    >
                                        {renderRangeText(active)}
                                    </DemiLightText>
                                    <MediumText
                                        fontSize={14}
                                        lineHeight={20}
                                        letterSpacing={-0.3}
                                        color={colors.primary}
                                    >
                                        {formatCommissionRateText(active.commissionRate, commissionUnit)}
                                    </MediumText>
                                </>
                            ) : (
                                <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.3} color="#6B7280">
                                    {brackets.length === 0 ? '구간 미설정' : '해당 구간 없음'}
                                </DemiLightText>
                            )}
                        </Row>
                    )}
                    <SvgIcon
                        name={isExpanded ? 'SvgUpStrokeArrow' : 'SvgDownStrokeArrow'}
                        size={12}
                        fill={colors.trans}
                        stroke="#71757C"
                        strokeWidth={2}
                    />
                </Row>
            </Row>

            {/* 펼침 영역 — 전체 표 */}
            {isExpanded && (
                <Col className="border-t border-solid border-[#E6E8EB]">
                    {brackets.length === 0 ? (
                        <Col className="px-[14px] py-[14px] items-center">
                            <DemiLightText fontSize={13} lineHeight={18} color="#6B7280">
                                매출 구간이 설정되지 않았습니다
                            </DemiLightText>
                        </Col>
                    ) : (
                        <>
                            <Row className="px-[14px] py-[8px] bg-[#F6F8FA]">
                                <div className="flex-1">
                                    <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.3} color="#6B7280">
                                        매출 구간 (만원)
                                    </DemiLightText>
                                </div>
                                <div className="w-[80px] text-right">
                                    <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.3} color="#6B7280">
                                        {commissionUnit === 'fixed' ? '회당 정산액' : '요율'}
                                    </DemiLightText>
                                </div>
                            </Row>
                            {brackets.map((bracket, idx) => {
                                const isActive = idx === activeIdx;
                                return (
                                    <Row
                                        key={`${bracket.minRevenue}-${bracket.maxRevenue}-${idx}`}
                                        className="px-[14px] py-[10px] items-center"
                                        style={{
                                            backgroundColor: isActive ? '#F2F6FF' : 'white',
                                            borderTop: idx === 0 ? 'none' : '1px solid #F2F4F6',
                                        }}
                                    >
                                        <div className="flex-1 flex items-center">
                                            {isActive && (
                                                <div className="bg-primary rounded-full px-[6px] h-[18px] mr-[8px] flex items-center justify-center">
                                                    <MediumText fontSize={13} lineHeight={18} color="white">
                                                        현재
                                                    </MediumText>
                                                </div>
                                            )}
                                            <DemiLightText
                                                fontSize={13}
                                                lineHeight={18}
                                                letterSpacing={-0.3}
                                                color={isActive ? colors.primary : colors.Text_1}
                                            >
                                                {renderRangeText(bracket)}
                                            </DemiLightText>
                                        </div>
                                        <div className="w-[80px] text-right">
                                            <MediumText
                                                fontSize={13}
                                                lineHeight={18}
                                                letterSpacing={-0.3}
                                                color={isActive ? colors.primary : colors.Text_1}
                                            >
                                                {formatCommissionRateText(bracket.commissionRate, commissionUnit)}
                                            </MediumText>
                                        </div>
                                    </Row>
                                );
                            })}
                        </>
                    )}
                </Col>
            )}
        </Col>
    );
};

// 기타 수당 추가하기
const AddButton = ({ onClick }: { onClick: () => void }) => {
    // 호버 됐을때
    const [isHover, setIsHover] = React.useState(false);
    return (
        <Row
            className=" items-center self-center cursor-pointer"
            onClick={onClick}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
        >
            <SvgIcon
                name="SvgPlusInCircle"
                size={20}
                fill={isHover ? colors.primary : colors.Text_1}
                className="mr-[11px]"
            />
            <DemiLightText
                fontSize={17}
                lineHeight={24.64}
                letterSpacing={-0.32}
                color={isHover ? colors.primary : colors.Text_1}
            >
                기타 수당 추가하기(ex. 블로그 수당)
            </DemiLightText>
        </Row>
    );
};
